import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  useTheme,
  IconButton,
  CircularProgress,
  Link,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getBookieLogoUrl, getSportsIcon } from "../../constants";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { Link as RouterLink } from "react-router-dom";

import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LinkIcon from "@mui/icons-material/Link";
import CalculateIcon from "@mui/icons-material/Calculate";
import ClearIcon from "@mui/icons-material/Clear";
import CheckIcon from "@mui/icons-material/Check";

const GroupedBetModal = ({
  open,
  onClose,
  groupedBetData,
  loading,
  error,
  bet,
  fullBet,
  league,
  market,
  event,
  event_id,
  evMode,
  onRefetchGroupedBet,
  excludedBookies = [],
}) => {
  const theme = useTheme();
  const [sortConfig, setSortConfig] = useState({
    key: bet,
    direction: "desc",
  });
  const [fairOddsPerSelection, setFairOddsPerSelection] = useState({});
  const [allBets, setAllBets] = useState([]);

  // Reset sort config when modal closes or bet changes
  useEffect(() => {
    if (bet) {
      setSortConfig({
        key: bet,
        direction: "desc",
      });
    }
  }, [bet, open]);

  // Update fair odds values when groupedBetData changes
  useEffect(() => {
    if (groupedBetData?.bets && evMode) {
      const fairOddsMap = {};

      // Extract unique selections
      const selectionsSet = new Set();
      groupedBetData.bets.forEach((bookieBet) => {
        bookieBet.selections.forEach((selection) => {
          selectionsSet.add(selection.bet);
        });
      });

      // For each unique selection, get the fair odds from the first bookie
      Array.from(selectionsSet).forEach((selectionName) => {
        for (const bookieBet of groupedBetData.bets) {
          const selection = bookieBet.selections.find(
            (s) => s.bet === selectionName
          );
          if (selection && selection.fairOdds) {
            fairOddsMap[selectionName] = selection.fairOdds;
            break;
          }
        }
      });

      setFairOddsPerSelection(fairOddsMap);

      // Store all bets to keep track of excluded bookies
      setAllBets((prevBets) => {
        // Merge current bets with previous bets that are now excluded
        const currentBookies = new Set(
          groupedBetData.bets.map((bet) => bet.bookie)
        );
        const excludedPrevBets = prevBets.filter(
          (bet) =>
            excludedBookies.includes(bet.bookie) &&
            !currentBookies.has(bet.bookie)
        );

        return [...groupedBetData.bets, ...excludedPrevBets];
      });
    }
  }, [groupedBetData, evMode, excludedBookies]);

  const sortedBets = useMemo(() => {
    // Use allBets instead of groupedBetData.bets to include excluded bookies
    const bets = allBets.length > 0 ? allBets : groupedBetData?.bets || [];

    return [...bets].sort((a, b) => {
      if (evMode) {
        const aSelection = a.selections.find((s) => s.bet === sortConfig.key);
        const bSelection = b.selections.find((s) => s.bet === sortConfig.key);

        // If no matching selection found, put it at the end
        if (!aSelection) return 1;
        if (!bSelection) return -1;

        // Sort by rate
        const multiplier = sortConfig.direction === "asc" ? 1 : -1;
        return (aSelection.rate - bSelection.rate) * multiplier;
      } else {
        // For non-EV mode, sort by rate directly
        const multiplier = sortConfig.direction === "asc" ? 1 : -1;
        return (a.selections[0].rate - b.selections[0].rate) * multiplier;
      }
    });
  }, [groupedBetData?.bets, allBets, sortConfig, evMode]);

  // Get unique bet types for columns
  const uniqueBets = useMemo(() => {
    if (!evMode) {
      // In non-EV mode, we only have one bet type
      return [market];
    }
    const betSet = new Set();
    sortedBets.forEach((bet) => {
      bet.selections.forEach((selection) => {
        betSet.add(selection.bet);
      });
    });
    return Array.from(betSet);
  }, [sortedBets, evMode, market]);

  const handleSort = (betType) => {
    setSortConfig((prevConfig) => ({
      key: betType,
      direction:
        prevConfig.key === betType && prevConfig.direction === "desc"
          ? "asc"
          : "desc",
    }));
  };

  const handleToggleBookmaker = (bookieName) => {
    if (onRefetchGroupedBet && evMode) {
      const newExcludedBookies = excludedBookies.includes(bookieName)
        ? excludedBookies.filter((b) => b !== bookieName)
        : [...excludedBookies, bookieName];

      onRefetchGroupedBet(newExcludedBookies);
    }
  };

  if (!groupedBetData) return null;

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  if (error || !groupedBetData?.bets) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogContent>
          <Typography color="error">
            {error ? "Error loading bet data" : "No bet data available"}
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  const BookieLogo = ({ bookie, hasSource }) => {
    const [imageError, setImageError] = useState(false);
    const theme = useTheme();
    const isExcluded = excludedBookies.includes(bookie);

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          height: "100%",
          opacity: isExcluded ? 0.5 : 1,
        }}
      >
        {imageError ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              borderRadius: 1,
              py: 0.5,
              px: 1,
            }}
          >
            <AccountBalanceWalletIcon
              sx={{ fontSize: 16, color: theme.palette.primary.main }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <img
              src={getBookieLogoUrl(bookie)}
              alt={bookie}
              onError={() => setImageError(true)}
              style={{
                height: 32,
                objectFit: "contain",
                borderRadius: "4px",
              }}
            />
            {hasSource && (
              <LinkIcon
                sx={{
                  fontSize: "0.9rem",
                  color: theme.palette.primary.main,
                  opacity: 0.7,
                }}
              />
            )}
          </Box>
        )}
        {evMode && (
          <IconButton
            size="small"
            color="primary"
            sx={{ ml: "auto" }}
            onClick={(e) => {
              e.stopPropagation();
              handleToggleBookmaker(bookie);
            }}
          >
            {isExcluded ? (
              <CheckIcon fontSize="small" />
            ) : (
              <ClearIcon fontSize="small" />
            )}
          </IconButton>
        )}
      </Box>
    );
  };

  // Helper function to render the odds cell content
  const renderOddsCell = (selection, betType) => {
    if (!selection) return null;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.9rem",
            fontWeight: evMode && selection.bet === bet ? 700 : 400,
            color: theme.palette.text.primary,
          }}
        >
          {selection.rate?.toFixed(2)}
          {evMode && selection.deviggedOdds
            ? ` (${selection.deviggedOdds?.toFixed(2)})`
            : ""}
        </Typography>
        {selection?.oddsEstimated === 1 && (
          <CalculateIcon
            sx={{
              fontSize: "1.4rem",
              color: theme.palette.text.secondary,
              opacity: 0.7,
            }}
          />
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          background: theme.palette.background.paper,
        },
      }}
    >
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {!!evMode && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "24px",
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(145deg, ${theme.palette.secondary.dark}, rgba(0, 0, 0, 0.2))`
                  : `linear-gradient(145deg, ${theme.palette.secondary.light}, rgba(255, 255, 255, 0.9))`,
              borderRadius: "16px",
              flexDirection: "column",
              position: "relative",
              overflow: "hidden",
              boxShadow: "inset 0 0 20px rgba(0, 0, 0, 0.1)",
              border: `1px solid ${theme.palette.divider}`,
              mb: 3,
              gap: 2,
            }}
          >
            <Typography variant="body1" sx={{ textAlign: "left", mb: 2 }}>
              This is the list of bookmakers included in the Fair Odds
              calculation for this value bet. For more information on how we
              calculate Fair Odds, click
              <Link
                href={
                  evMode
                    ? "https://help.bonusbank.com.au/article/393-how-are-fair-odds-calculated"
                    : "https://help.bonusbank.com.au/article/394-how-do-non-ev-value-bets-work"
                }
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.palette.primary.main,
                  textDecoration: "none",
                  fontWeight: 500,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                  ml: 0.5,
                }}
              >
                here
              </Link>
            </Typography>

            {evMode && (
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  alignItems: "center",
                  alignSelf: "flex-start",
                  mt: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CalculateIcon
                    sx={{
                      fontSize: "1.4rem",
                      color: theme.palette.text.secondary,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    = Estimated odds
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ClearIcon
                    sx={{
                      fontSize: "1.4rem",
                      color: theme.palette.text.secondary,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    = Exclude from fair odds calculation
                  </Typography>
                </Box>
              </Box>
            )}

            {evMode && Object.keys(fairOddsPerSelection).length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  mt: 2,
                  flexWrap: "wrap",
                  justifyContent: "center",
                  width: "100%",
                  backgroundColor: alpha(theme.palette.background.paper, 0.6),
                  p: 2,
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, width: "100%", textAlign: "center" }}
                >
                  Fair Odds
                </Typography>
                {uniqueBets.map((selection) => (
                  <Chip
                    key={selection}
                    label={`${selection}: ${
                      fairOddsPerSelection[selection]?.toFixed(3) || "N/A"
                    }`}
                    variant="outlined"
                    color="primary"
                    size="medium"
                    sx={{ fontWeight: selection === bet ? 600 : 400 }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mb: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {getSportsIcon(league?.split(" ")[0])
              ? React.cloneElement(getSportsIcon(league?.split(" ")[0]), {
                  sx: { color: theme.palette.text.secondary },
                })
              : null}
            <Typography
              variant="body1"
              sx={{ color: theme.palette.text.secondary }}
            >
              {league}
            </Typography>
            <Chip
              label={market}
              size="small"
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              component={RouterLink}
              to={`/event/${event_id}?market=${encodeURIComponent(market)}`}
              startIcon={<LinkIcon />}
              sx={{
                textTransform: "none",
                color: theme.palette.text.primary,
                p: 0,
                "&:hover": {
                  backgroundColor: "transparent",
                  textDecoration: "underline",
                },
              }}
            >
              {event}
            </Button>
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.primary.main,
            }}
          >
            {fullBet}
          </Typography>
        </Box>
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Bookmaker</TableCell>
                {uniqueBets.map((betType) => (
                  <TableCell
                    key={betType}
                    align="right"
                    onClick={() => handleSort(betType)}
                    sx={{
                      cursor: "pointer",
                      userSelect: "none",
                      "&:hover": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.04
                        ),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: betType === bet ? 700 : 400,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {betType}
                      </Typography>
                      {sortConfig.key === betType &&
                        (sortConfig.direction === "desc" ? (
                          <ArrowDownwardIcon sx={{ fontSize: "1rem" }} />
                        ) : (
                          <ArrowUpwardIcon sx={{ fontSize: "1rem" }} />
                        ))}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedBets.map((betItem) => {
                const rowClickable = betItem.selections[0]?.source;
                const isExcluded = excludedBookies.includes(betItem.bookie);

                return (
                  <TableRow
                    key={betItem.bookie}
                    hover={Boolean(rowClickable)}
                    onClick={() =>
                      rowClickable && window.open(betItem.selections[0].source)
                    }
                    sx={{
                      cursor: rowClickable ? "pointer" : "default",
                      "&:hover": rowClickable
                        ? {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.04
                            ),
                          }
                        : {},
                      opacity: isExcluded ? 0.5 : 1,
                      backgroundColor: isExcluded
                        ? alpha(theme.palette.action.disabledBackground, 0.1)
                        : "inherit",
                    }}
                  >
                    <TableCell>
                      <BookieLogo
                        bookie={betItem.bookie}
                        hasSource={Boolean(betItem.selections[0]?.source)}
                      />
                    </TableCell>
                    {uniqueBets.map((betType) => {
                      const selection = evMode
                        ? betItem.selections.find((s) => s.bet === betType)
                        : betItem.selections[0];

                      return (
                        <TableCell key={betType} align="right">
                          {renderOddsCell(selection, betType)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default GroupedBetModal;
